import { useAuthUser } from "../../../contexts/AuthUserContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallData } from "../../../contexts/CallDataContext";
import ChatLogs from "../../../components/chatHistory/chatLogs";
import ChatDetails from "../../../components/chatHistory/chatDetails";
import ExportToCSV from "../../../components/chatHistory/exportToCSV";
import React, { useState, useEffect } from "react"; 
import { useSearchParams, useNavigate } from "react-router-dom";

import { useSentiment } from "../../../contexts/SentimentContext";
import { useWeek } from "../../../contexts/WeekContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../../utils/dateUtils";
import { getOrderStatus, getEquipmentList } from "../../../utils/bookingUtils";
import ChatbotWidget from "../../../components/chatbot-embed/ChatbotWidget";

const ChatHistory = () => {
    const user = useAuthUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        stats: false,
        filters: false,
        table: false
    });

    const { data, loading, error } = useCallData();
    const { sentimentCounts } = useSentiment();
    const { dateRange } = useWeek();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Add filter states
    const [activeFilters, setActiveFilters] = useState({
        startTime: null,
        successEval: null,
        sentiment: null,
    });

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        const chatId = searchParams.get('id');
        if (chatId && data) {
            const chat = data.find(c => c.id === chatId);
            if (chat) {
                setSelectedChat(chat);
            }
        }

        // Initialize filters from URL
        const urlFilters = {};
        const filterKeys = ['startTime', 'successEval', 'sentiment'];
        
        filterKeys.forEach(key => {
            const value = searchParams.get(key);
            if (value !== null) {
                urlFilters[key] = value;
            }
        });

        if (Object.keys(urlFilters).length > 0) {
            setActiveFilters(prev => ({ ...prev, ...urlFilters }));
            // Set active filter based on what's in URL
            const activeKey = Object.keys(urlFilters).find(key => urlFilters[key] !== null);
            if (activeKey) {
                setActiveFilter(activeKey);
            }
        }
    }, [data, searchParams]);

    // Handle direct URL navigation when component mounts
    useEffect(() => {
        if (data && data.length > 0) {
            const chatId = searchParams.get('id');
            if (chatId) {
                const chat = data.find(c => c.id === chatId);
                if (chat) {
                    setSelectedChat(chat);
                }
            }
        }
    }, [data, searchParams]);

    // Add this function to filter chats by date range and then by active filters
    const getFilteredChats = () => {
        if (!data || !dateRange?.start || !dateRange?.end) return [];

        // First, filter by date range
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);

        let filtered = data.filter(chat => {
            const chatDate = parseCustomTimestamp(chat?.metadata?.timestamp);
            if (!chatDate) return false;
            return isWithinInterval(chatDate, { start: rangeStart, end: rangeEnd });
        });

        // Then, apply active filters
        // Filter by success evaluation (Pass/Partial/Fail)
        if (activeFilters.successEval) {
            filtered = filtered.filter(chat => {
                const score = chat.evaluation?.success_score ?? 0;
                const status = score >= 7 ? 'pass' : score >= 4 ? 'partial' : 'fail';
                return status === activeFilters.successEval.toLowerCase();
            });
        }

        // Filter by sentiment (positive/negative/neutral)
        if (activeFilters.sentiment) {
            filtered = filtered.filter(chat => {
                const sentiment = (chat.analytics?.user_sentiment || 'neutral').toLowerCase();
                return sentiment === activeFilters.sentiment.toLowerCase();
            });
        }

        // Sorting logic
        if (activeFilters.startTime) {
            filtered.sort((a, b) => {
                const timeA = a.metadata?.timestamp || '';
                const timeB = b.metadata?.timestamp || '';
                return activeFilters.startTime === 'asc' ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
            });
        } else {
            // Default sort by timestamp (latest first)
            filtered.sort((a, b) => {
                const timeA = a.metadata?.timestamp || '';
                const timeB = b.metadata?.timestamp || '';
                return timeB.localeCompare(timeA); // This will sort in descending order (latest first)
            });
        }

        return filtered;
    };

    // Use this function where you need filtered chat counts
    const filteredChats = getFilteredChats();
    const totalFilteredChats = filteredChats.length;

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, stats: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, filters: true }));
        }, 500));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, table: true }));
        }, 700));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // If we have a selected chat, reset to show only logs
            if (selectedChat !== null) {
                setSelectedChat(null);
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [selectedChat]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            // Get current URL parameters
            const currentParams = new URLSearchParams(window.location.search);
            const chatId = currentParams.get('id');
            
            // Handle chat details state
            if (selectedChat && !chatId) {
                // Close chat details when back button removes ID
                setSelectedChat(null);
            } else if (!selectedChat && chatId && data) {
                // Open chat details when forward button adds ID
                const chat = data.find(c => c.id === chatId);
                if (chat) {
                    setSelectedChat(chat);
                }
            }
            
            // Handle filter changes
            const currentFilters = {};
            const filterKeys = ['startTime', 'successEval', 'sentiment'];
            
            filterKeys.forEach(key => {
                const value = currentParams.get(key);
                if (value !== null) {
                    currentFilters[key] = value;
                } else {
                    currentFilters[key] = null;
                }
            });
            
            // Update filters
            setActiveFilters(currentFilters);
            
            // Update active filter
            const activeKey = Object.keys(currentFilters).find(key => currentFilters[key] !== null);
            setActiveFilter(activeKey || null);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [selectedChat, data]);

    console.log(selectedChat?.id);

    // Track the currently active filter
    const [activeFilter, setActiveFilter] = useState(null);

    // Mobile filters state
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Chatbot state
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);

    const handleChatSelection = (chat) => {
        // Add to browser history when opening chat details
        const newParams = new URLSearchParams(searchParams);
        newParams.set('id', chat.id);
        window.history.pushState({}, '', `?${newParams.toString()}`);
        setSelectedChat(chat);
    };

    const handleCloseChatDetails = () => {
        // Add to browser history when closing chat details
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id');
        window.history.pushState({}, '', `?${newParams.toString()}`);
        setSelectedChat(null);
    };

    // Filter configuration
    const filterConfig = [
        {
            id: 'startTime',
            label: 'Start Time',
            icon: 'stash:data-date'
        },
        {
            id: 'sentiment',
            label: 'Sentiment',
            icon: 'material-symbols:add-reaction-outline'
        },
        {
            id: 'successEval',
            label: 'Success',
            icon: 'line-md:check-all'
        }
    ];

    // Get available status types from orders
    const getAvailableStatusTypes = () => {
        if (!data || data.length === 0) return ['Booking Confirmed', 'Pending', 'Cancelled'];

        const allOrdersData = data.filter(chat => chat.booking_info);
        const statusTypes = [...new Set(allOrdersData.map(order => getOrderStatus(order.booking_info)))];
        return statusTypes.length > 0 ? statusTypes : ['Booking Confirmed', 'Pending', 'Cancelled'];
    };

    // Handle status selection
    const handleStatusSelect = (status) => {
        setActiveFilters({
            ...activeFilters,
            orderStatus: status,
        });
        setActiveFilter('orderStatus');
        setShowStatusDropdown(false);
    };

    // Handle filter clicks
    const handleFilterClick = (filterName) => {
        let newFilters = { ...activeFilters };
        let newActiveFilter = activeFilter;

        if (filterName === 'startTime') {
            // If clicking the same filter, toggle its direction
            if (activeFilter === filterName) {
                newFilters[filterName] = activeFilters[filterName] === 'asc' ? 'desc' : 'asc';
            } else {
                // If clicking a different filter, deactivate current and activate new one
                newFilters = {
                    startTime: 'asc',
                    successEval: null,
                    sentiment: null,
                };
                newActiveFilter = filterName;
            }
        } else if (filterName === 'sentiment') {
            // Sentiment filter: cycle through positive -> negative -> neutral -> off
            if (activeFilter === 'sentiment') {
                const currentSentiment = activeFilters.sentiment?.toLowerCase();
                if (currentSentiment === 'positive') {
                    newFilters.sentiment = 'negative';
                } else if (currentSentiment === 'negative') {
                    newFilters.sentiment = 'neutral';
                } else {
                    newFilters.sentiment = null;
                    newActiveFilter = null;
                }
            } else {
                newFilters = {
                    startTime: null,
                    successEval: null,
                    sentiment: 'positive',
                };
                newActiveFilter = 'sentiment';
            }
        } else if (filterName === 'successEval') {
            // Success filter: cycle through pass -> partial -> fail -> off
            if (activeFilter === 'successEval') {
                const currentStatus = activeFilters.successEval?.toLowerCase();
                if (currentStatus === 'pass') {
                    newFilters.successEval = 'partial';
                } else if (currentStatus === 'partial') {
                    newFilters.successEval = 'fail';
                } else {
                    newFilters.successEval = null;
                    newActiveFilter = null;
                }
            } else {
                newFilters = {
                    startTime: null,
                    successEval: 'pass',
                    sentiment: null,
                };
                newActiveFilter = 'successEval';
            }
        }

        // Update state
        setActiveFilters(newFilters);
        setActiveFilter(newActiveFilter);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value !== null) {
                newParams.set(key, value.toString());
            } else {
                newParams.delete(key);
            }
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveFilters({
            startTime: null,
            successEval: null,
            sentiment: null,
        });
        setActiveFilter(null);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        Object.keys(activeFilters).forEach(key => {
            newParams.delete(key);
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Check if any filter is active
    const isAnyFilterActive = Object.values(activeFilters).some(val => val !== null);

    // Filter Button Component
    const FilterButton = ({ id, label, icon }) => {
        return (
            <button
                onClick={() => handleFilterClick(id)}
                className={`flex w-fit whitespace-nowrap flex-row items-center font-medium gap-3 justify-between px-3 py-2 ${activeFilter === id ? 'bg-primary-20 border-primary-30 border' : 'bg-background xl:button-container'} rounded-md text-sm`}
            >
                <span className="text-font-primary flex flex-row items-center gap-1">
                    <Icon icon={icon} className="text-xl text-accent" />
                    {label}
                    {activeFilter === id && id === 'sentiment' && (
                        <span className="text-xs text-font-secondary ml-1 capitalize">
                            ({activeFilters.sentiment ? activeFilters.sentiment.charAt(0).toUpperCase() + activeFilters.sentiment.slice(1) : 'off'})
                        </span>
                    )}
                    {activeFilter === id && id === 'successEval' && (
                        <span className="text-xs text-font-secondary ml-1 capitalize">
                            ({activeFilters.successEval ? activeFilters.successEval.charAt(0).toUpperCase() + activeFilters.successEval.slice(1) : 'off'})
                        </span>
                    )}
                </span>
                {activeFilter === id && id === 'startTime' && (
                    <Icon
                        icon={activeFilters[id] === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'}
                        className="text-xl text-accent"
                    />
                )}
                {activeFilter === id && (id === 'sentiment' || id === 'successEval') && (
                    <Icon
                        icon="material-symbols:check-circle-outline"
                        className="text-xl text-accent"
                    />
                )}
            </button>
        );
    };

    return (
        <div className={`
            responsive-page-layout scrollable
            h-full max-h-screen overflow-hidden
        `}>
            <div className={`dashboard-header-responsive flex-shrink-0 transition-all duration-700 ease-out ${
                    animationStates.header
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    <div className="header-textbox">
                        <Icon icon="ph:chats-fill" className="header-icon"></Icon>
                        Chat History
                    </div>
                    <div className="header-right-3 hidden xl:flex">
                        <div className='flex flex-col sm:flex-row gap-2 w-full lg:w-fit'>
                            <div className="flex flex-row gap-1 items-center text-xs sm:text-sm p-2 rounded-md bg-bgSecondary border border-border">
                                <Icon icon="mingcute:ai-fill" className="text-lg sm:text-xl text-secondary"></Icon>
                                <p><strong>AI Assistant:</strong> Kronk</p>
                            </div>
                            {/* <div className={`
                                flex flex-row gap-1 items-center text-xs sm:text-sm p-2 rounded-md button-container
                                cursor-pointer hover:bg-neo-button transition-colors`}
                                onClick={() => setIsChatbotOpen(true)}
                            >
                                <Icon icon="material-symbols:chat" className="text-lg sm:text-xl text-secondary"></Icon>
                                <p className="hidden sm:inline font-semibold">Chat with Assistant</p>
                            </div> */}
                        </div>
                    </div>
            </div>
            {/* Main Content - Hidden on small screens when ChatDetails is shown */}
            <div className={`
                calls-tab-layout flex-1 flex flex-col gap-4 w-full min-h-0
            `}>
                {/* Summary Data or Breadcrumbs */}
                {!selectedChat ? (
                    <div className={`hidden xl:flex flex-row w-full gap-3 transition-all duration-700 ease-out ${
                        animationStates.stats
                            ? 'opacity-100 translate-y-0 scale-100'
                            : 'opacity-0 translate-y-4 scale-95'
                    }`}>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="material-symbols:chat" className="text-xl text-yellow-400"></Icon>
                                Total Chats
                            </div>
                            <p className="text-yellow-400 font-semibold text-lg">{filteredChats.length}</p>
                        </div>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="icon-park-outline:positive-dynamics" className="text-xl text-green-500"></Icon>
                                Improving
                            </div>
                            <p className="text-green-500 font-semibold text-lg">{sentimentCounts.improving}</p>
                        </div>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="icon-park-outline:negative-dynamics" className="text-xl text-accent"></Icon>
                                Declining
                            </div>
                            <p className="text-accent font-semibold text-lg">{sentimentCounts.declining}</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 text-font-primary">
                        <button
                            onClick={handleCloseChatDetails}
                            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
                            title="Go back to chat history"
                        >
                            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                        </button>
                        <span 
                            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
                            onClick={handleCloseChatDetails}
                        >
                            <Icon icon="material-symbols:chat-history-rounded" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
                            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Chat History</span>
                        </span>
                        <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
                        <span className="flex items-center justify-center gap-1">
                            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
                            <span className="text-font-primary text-base">
                                Chat Details
                            </span>
                        </span>
                    </div>
                )}
                {/* New layout for Chat History */}
                <div className="flex flex-1 gap-2 w-full min-h-0">
                    <div className={`
                        flex flex-col w-full gap-4 min-w-0 min-h-0 transition-all duration-300 ease-out
                        ${selectedChat ? 'hidden 2xl:flex 2xl:w-full' : 'flex'}
                        ${animationStates.filters
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-4'
                        }
                    `}>
                        {/* Filters */}
                        <div className={`w-full flex flex-row items-start sm:items-center ${user.role === 'helios_admin' ? 'justify-between' : 'sm:justify-start'} gap-2`}>

                            {/* Desktop Filters (xl and above) */}
                            <div className={`call-filters hidden xl:flex flex-row gap-2 items-center w-fit ${selectedChat ? '2xl:hidden' : ''}`}>
                                {filterConfig.map(filter => (
                                    <FilterButton
                                        key={filter.id}
                                        id={filter.id}
                                        label={filter.label}
                                        icon={filter.icon}
                                    />
                                ))}
                                {isAnyFilterActive && (
                                    <button
                                        onClick={resetFilters}
                                        className="flex flex-row items-center gap-2 p-2 button-container whitespace-nowrap text-sm ml-auto rounded-md transition-colors"
                                    >
                                        <Icon icon="material-symbols:refresh" className="text-xl text-accent" />
                                        Reset Filters
                                    </button>
                                )}
                            </div>

                            {/* Mobile All Filters Button (below xl) */}
                            <div className={`w-fit relative xl:hidden ${selectedChat ? '2xl:block' : ''}`}>
                                <button
                                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                                    className="flex flex-row items-center gap-2 p-2 button-container text-sm rounded-md transition-colors w-full justify-center"
                                >
                                    <Icon icon="material-symbols:filter-list" className="text-xl text-accent" />
                                    All Filters
                                    {isAnyFilterActive && (
                                        <span className="bg-primary text-white text-xs px-2 py-1 rounded-full ml-2">
                                            {Object.values(activeFilters).filter(Boolean).length}
                                        </span>
                                    )}
                                </button>

                                {/* Mobile Filters Dropdown */}
                                {showMobileFilters && (
                                    <div className="absolute top-full w-fit left-0 right-0 mt-2 button-container rounded-md shadow-lg border border-bgAccent z-50">
                                        <div className="flex flex-col ">
                                            {filterConfig.map(filter => (
                                                <FilterButton
                                                    key={filter.id}
                                                    id={filter.id}
                                                    label={filter.label}
                                                    icon={filter.icon}
                                                />
                                            ))}
                                            {isAnyFilterActive && (
                                                <button
                                                    onClick={() => {
                                                        resetFilters();
                                                        setShowMobileFilters(false);
                                                    }}
                                                    className="flex flex-row items-center gap-2 p-2 text-sm rounded-md transition-colors justify-center mt-2 border-t border-border"
                                                >
                                                    <Icon icon="material-symbols:refresh" className="text-xl text-error" />
                                                    Reset Filters
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Click outside to close mobile filters */}
                                {showMobileFilters && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowMobileFilters(false)}
                                    />
                                )}
                            </div>

                            {user.role === 'helios_admin' && (
                                <div className="w-fit sm:w-auto">
                                    <ExportToCSV data={data} selectedChat={selectedChat} selectedRows={selectedRows} />
                                </div>
                            )}
                        </div>
                        {/* Chat Logs */}
                        <div className="flex flex-1 gap-2 w-full min-h-0">
                            {loading && <div className="p-4 w-full text-center text-gray-400">Loading chat history...</div>}
                            {error && <div className="p-4 w-full text-center text-error">Error loading chat history: {error.message}</div>}
                            
                            {/* Only render the table when not loading and no error */}
                            {!loading && !error && (
                                <ChatLogs
                                    onRowClick={handleChatSelection}
                                    isShort={!!selectedChat}
                                    chatRow={selectedChat?.id}
                                    displayData={filteredChats}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    className={`w-full h-full flex-1 min-h-0 transition-all duration-700 ease-out ${
                                        animationStates.table
                                            ? 'opacity-100 translate-y-0'
                                            : 'opacity-0 translate-y-4'
                                    }`}
                                />
                            )}
                        </div>
                    </div>
                    {/* The ChatDetails content will only render when a chat is selected */}
                    {selectedChat && (
                        <div className="w-full flex flex-col 2xl:w-1/2 flex-shrink-0 min-h-0 max-h-screen overflow-hidden">
                            <ChatDetails chat={selectedChat} onClose={handleCloseChatDetails} />
                        </div>
                    )}
                </div>
            </div>
            
            {/* Chatbot Widget */}
            <ChatbotWidget 
                isOpen={isChatbotOpen} 
                onClose={() => setIsChatbotOpen(false)} 
            />
        </div>
    );
};

export default ChatHistory;
